import { HidePassword, ShowPassword } from 'assets/Icons';
import { Button, Spacer } from 'components/atoms';
import { InputField } from 'components/atoms/InputField/InputField';
import SEARCH_PARAMS from 'constants/searchParams';
import { FORGOT_PASSWORD, SIGN_UP } from 'navigation/routes';
import { useMutation } from 'network/mutations/useMutationHook';
import { login } from 'network/services/auth';
import { isAccountNotVerifiedLoginError } from 'utils/customerAuth';
import { enqueueSnackbar } from 'notistack';
import { ChangeEvent, useState } from 'react';
import 'react-phone-input-2/lib/style.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { userIsLoggedIn, userRoleAtom } from 'store/atoms/userAtom';
import { validators } from 'utils/validator';
import { AuthContainer, InputCon, TogglePassword, extraStylesInput } from './styles';

const Login = () => {
  const [searchParams] = useSearchParams();
  const setUserRole = useSetRecoilState(userRoleAtom);
  const setUserStatus = useSetRecoilState(userIsLoggedIn);
  const [formData, setFormData] = useState({ identifier: '', password: '' });
  const [formError, setFormError] = useState({
    identifier: '',
    password: ''
  });

  const handleAuthSuccess = (data: any) => {
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('is_login', 'true');
    localStorage.setItem('user', JSON.stringify(data.user));
    setUserRole(data.user.role);
    setUserStatus(true);

    enqueueSnackbar('Login is successful', {
      variant: 'success',
      autoHideDuration: 3000
    });

    // Check for return URL from session expiration or search params
    const savedReturnUrl = localStorage.getItem('return_url');
    const redirectParam = searchParams.get('redirect');
    const reroute = searchParams.get(SEARCH_PARAMS.LOGIN_SUCCESS_REROUTE);

    // Clear the saved return URL
    if (savedReturnUrl) {
      localStorage.removeItem('return_url');
    }

    // Priority: saved return URL > redirect param > reroute param > home
    if (savedReturnUrl && savedReturnUrl !== '/login') {
      navigate(savedReturnUrl);
    } else if (redirectParam && redirectParam !== '/login') {
      navigate(decodeURIComponent(redirectParam));
    } else if (reroute !== null) {
      navigate(`${reroute}?refresh=True`);
    } else {
      navigate('/');
    }
  };

  const onUpdateFormData = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;
    const error = validators[name](value);
    setFormData(formData => ({ ...formData, [name]: value }));
    setFormError({
      ...formError,
      [name]: error
    });
  };

  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState<'text' | 'password'>('password');

  const onTogglePassword = () => {
    if (showPassword === 'text') return setShowPassword('password');
    setShowPassword('text');
  };

  const { mutateAsync, isLoading } = useMutation(login, 'LOGIN');

  // Sign in with Google is commented out on the login page.
  // const googleBtnRef = useRef<HTMLDivElement | null>(null);
  //
  // useEffect(() => {
  //   const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
  //   if (!clientId) return;
  //
  //   const existing = document.getElementById('google-identity-script');
  //   if (!existing) {
  //     const script = document.createElement('script');
  //     script.id = 'google-identity-script';
  //     script.src = 'https://accounts.google.com/gsi/client';
  //     script.async = true;
  //     script.defer = true;
  //     document.body.appendChild(script);
  //     script.onload = () => {
  //       if (!window.google) return;
  //       window.google?.accounts?.id.initialize({
  //         client_id: clientId,
  //         callback: handleCredentialResponse,
  //       });
  //
  //       if (googleBtnRef.current) {
  //         window.google?.accounts?.id.renderButton(googleBtnRef.current, {
  //           theme: 'outline',
  //           size: 'large',
  //           text: 'signin_with',
  //           shape: 'rectangular',
  //         });
  //       }
  //     };
  //   } else if (window.google) {
  //     window.google?.accounts?.id.initialize({
  //       client_id: clientId,
  //       callback: handleCredentialResponse,
  //     });
  //     if (googleBtnRef.current) {
  //       window.google?.accounts?.id.renderButton(googleBtnRef.current, {
  //         theme: 'outline',
  //         size: 'large',
  //         text: 'signin_with',
  //         shape: 'rectangular',
  //       });
  //     }
  //   }
  // }, []);
  //
  // async function handleCredentialResponse(response: any) {
  //   const idToken = response?.credential;
  //   if (!idToken) return;
  //
  //   try {
  //     const base = process.env.REACT_APP_BASE_URL || '';
  //     const res = await axios.post(`${base}/api/v1/auth/google`, { id_token: idToken });
  //     if (res.data?.status === 'success') {
  //       handleAuthSuccess(res.data);
  //     } else {
  //       enqueueSnackbar('Google sign-in failed', { variant: 'error' });
  //     }
  //   } catch (err: any) {
  //     enqueueSnackbar(`Google sign-in error: ${err?.response?.data?.message || err.message}`, { variant: 'error' });
  //   }
  // }
  //
  // const googleLogin = () => {
  //   if (window.google?.accounts?.id) {
  //     try {
  //       window.google?.accounts?.id.prompt();
  //     } catch (e) {
  //       const btn = googleBtnRef.current?.querySelector('div');
  //       if (btn && (btn as HTMLElement).click) (btn as HTMLElement).click();
  //     }
  //   } else {
  //     enqueueSnackbar('Google SDK not loaded', { variant: 'error' });
  //   }
  // };

  const _login = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    let formattedValue = formData.identifier;

    if (formData.identifier.length === 11 && /^\d+$/.test(formData.identifier)) {
      formattedValue = `234${formData.identifier.slice(1)}`;
    }
    const credentials = {
      identifier: formData.identifier,
      email: formattedValue.toLowerCase(),
      password: formData.password
    };

    try {
      const { data } = await mutateAsync(credentials);
      if (data.status === 'success') {
        handleAuthSuccess(data);
      }
    } catch (error: any) {
      if (isAccountNotVerifiedLoginError(error)) {
        enqueueSnackbar('Account not verified. Enter the code from your email or resend OTP.', {
          variant: 'warning'
        });
        navigate(`/otp/${encodeURIComponent(credentials.email)}`);
        return;
      }
      enqueueSnackbar(error?.response?.data?.message || 'Login failed', { variant: 'error' });
    }
  };

  const disabled = !formData.identifier || !formData.password || !!validators.identifier(formData.identifier);

  return (
    <AuthContainer>
      <h1 className="text-center">Login to your account</h1>
      <p className="option text-center">
        Don't have an account? <span onClick={() => navigate(SIGN_UP)}>Create an Account</span>
      </p>

      <form className="">
        <InputCon>
          <span>Email Address or Phone number</span>
          <InputField
            type="email"
            name="identifier"
            value={formData.identifier}
            onChange={onUpdateFormData}
            placeholder="email address or phone number"
            extraStyles={extraStylesInput}
            required={true}
            error={formError.identifier}
            autoComplete="username"
          />
        </InputCon>
        <Spacer height="1.5rem" />
        <InputCon>
          <span>Password</span>
          <InputField
            type={showPassword}
            name="password"
            value={formData.password}
            onChange={onUpdateFormData}
            placeholder="password "
            extraStyles={extraStylesInput}
            action={
              <TogglePassword onClick={onTogglePassword}>
                {showPassword !== 'text' ? <ShowPassword /> : <HidePassword />}
              </TogglePassword>
            }
            autoComplete="current-password"
          />
        </InputCon>
        <Spacer height="4rem" />
        <div className="buttonContainer">
          <Button isLoading={isLoading} type="submit" onClick={_login} disabled={isLoading || disabled}>
            Log in
          </Button>
        </div>
        <div className="forgot">
          <p className="mt-3 tracking-wide text-[#454545] text-base md:text-lg cursor-pointer">
            <span onClick={() => navigate(FORGOT_PASSWORD)}>Forgot password ?</span>
          </p>
        </div>
      </form>

      {/* Sign in with Google
      <div className="flex items-center gap-3 w-[50%] mb-4 m-auto">
        <div className="w-full h-[1px] rounded-sm bg-[#D9D9D9]"></div>
        <p>Or</p>
        <div className="w-full h-[1px] rounded-sm bg-[#D9D9D9]"></div>
      </div>
      <div className="flex gap-2 justify-center w-full">
        <div
          className="rounded-full flex items-center justify-center"
          style={{
            background: '#fff',
            padding: 6,
            borderRadius: 9999,
            boxShadow: '0 2px 8px rgba(16,24,40,0.06)',
            overflow: 'hidden',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={googleLogin}
          aria-hidden
        >
          <div ref={googleBtnRef as any} style={{ minWidth: 140 }} />
        </div>
      </div>
      */}
    </AuthContainer>
  );
};

export default Login;